/**
 * This page's sends to one session's runtime that it took and never finished answering, by turn
 * id, with the clock that settles them. Such a send may have reached the runtime anyway, and a
 * read that began while it was on its way may have looked before the runtime saved it, so only a
 * read begun after the send gave up settles it: listed, the runtime got it after all; not listed,
 * it never did, and the read carries the send as a failed row with its words and files, whose
 * Retry sends it again under the same id (`CodeSessionTurn.unsent`). The clock counts every send
 * and reset, so a read's checkpoint says which sends it began before.
 */
export class UnconfirmedSends<Row> {
  private revision = 0;
  private sends = new Map<string, { row: Row; at: number }>();

  /** Marks the live state before a read begins, for `before`. */
  checkpoint(): number {
    return this.revision;
  }

  /** Counts a send the runtime answered, or a reset, so a later checkpoint is after it. */
  bump(): number {
    this.revision += 1;
    return this.revision;
  }

  /** A send the runtime took and never finished answering, as its failed row. */
  add(turnId: string, row: Row): void {
    this.revision += 1;
    // A Retry that gives up again keeps the time the send first gave up, so a read begun since
    // still carries its row.
    this.sends.set(turnId, { row, at: this.sends.get(turnId)?.at ?? this.revision });
  }

  /** The runtime answered a send of this id, late or sent again, so it stands as sent. */
  drop(turnId: string): void {
    this.sends.delete(turnId);
  }

  /** A read listed these turns: the runtime got their sends after all. */
  settle(listed: Iterable<string>): void {
    for (const id of listed) {
      this.sends.delete(id);
    }
  }

  /** The sends that gave up at or before `checkpoint` and that no read has listed, oldest first. */
  before(checkpoint: number): Row[] {
    return [...this.sends.values()].flatMap(({ row, at }) => (at <= checkpoint ? [row] : []));
  }

  /** Every send no read has settled, oldest first. */
  rows(): Row[] {
    return [...this.sends.values()].map(({ row }) => row);
  }
}

/**
 * `turns` with `unsent` among them by send time, each ahead of the first turn sent after it. A turn
 * stamped in the millisecond a send began goes before it: a runtime stamps a turn when it takes it
 * in or delivers it, so one the person sent after this send is stamped later.
 */
export function placeBySendTime<
  Turn extends { createdAt: string },
  Unsent extends { createdAt: string },
>(turns: readonly Turn[], unsent: readonly Unsent[]): (Turn | Unsent)[] {
  const sentAt = (turn: { createdAt: string }) => Date.parse(turn.createdAt);
  const placed: (Turn | Unsent)[] = [...turns];
  for (const row of [...unsent].sort((a, b) => sentAt(a) - sentAt(b))) {
    const next = placed.findIndex((turn) => sentAt(turn) > sentAt(row));
    placed.splice(next === -1 ? placed.length : next, 0, row);
  }
  return placed;
}
